import type { Money } from '@/utils/price'

/**
 * Типы для Clients module
 * Упрощённая доменная модель клиента на основе V1Client из API
 */

import type { Country } from '@/features/references/types/references.types'

/** Тип контакта - соответствует V1ContactType из API */
export enum ContactType {
  EMAIL = 'CONTACT_TYPE_EMAIL',
  PHONE = 'CONTACT_TYPE_PHONE',
  TELEGRAM = 'CONTACT_TYPE_TELEGRAM',
  WHATSAPP = 'CONTACT_TYPE_WHATSAPP',
  OTHER = 'CONTACT_TYPE_OTHER',
  INVALID = 'CONTACT_TYPE_INVALID',
}

/** Контакт клиента */
export interface Contact {
  type: ContactType
  value: string
}

/** Поддерживаемые валюты ввода (DEV-41). Каноника бюджета всегда USD. */
export type CurrencyCode = 'USD' | 'VND' | 'RUB'

export const SUPPORTED_CURRENCIES: CurrencyCode[] = ['USD', 'VND', 'RUB']

/** Минимальный бюджет клиента в USD (DEV-41). */
export const MIN_BUDGET_USD = 50_000

/** Информация о конвертации, если бюджет введён не в USD. */
export interface BudgetConversion {
  originalCurrency: CurrencyCode
  originalFrom: number
  originalTo: number
  /** 1 unit original_currency = exchangeRate USD. */
  exchangeRate: number
  /** ISO timestamp фиксации курса. */
  convertedAt: string
}

/**
 * Бюджет клиента. DEV-301 (реверс DEV-41): каноника — moneyFrom / moneyTo
 * (валюта менеджерского ввода, минорные единицы); fromUsd/toUsd и conversion —
 * вычисляются бэком на лету по текущему курсу (read-only).
 */
export interface Budget {
  /** Каноника: бюджет в валюте ввода (vinprop.common.v1.Money). */
  moneyFrom?: Money
  moneyTo?: Money
  /** @deprecated дублирует fromUsd, оставлено для обратной совместимости. */
  from?: number
  /** @deprecated дублирует toUsd. */
  to?: number
  /** @deprecated после DEV-41 всегда "USD". */
  currency?: string
  /** Вычисляемый USD-эквивалент (read-only, по текущему курсу). */
  fromUsd?: number
  toUsd?: number
  conversion?: BudgetConversion
}

/** Тип недвижимости - соответствует V1PropertyType из proto */
export enum PropertyType {
  APARTMENTS = 'PROPERTY_TYPE_APARTMENTS',
  VILLAS = 'PROPERTY_TYPE_VILLAS',
  COMMERCIAL = 'PROPERTY_TYPE_COMMERCIAL',
}

/** Цель покупки - соответствует V1PurchaseGoal из proto */
export enum PurchaseGoal {
  INVALID = 'PURCHASE_GOAL_INVALID',
  INVESTMENT = 'PURCHASE_GOAL_INVESTMENT',
  LIVING = 'PURCHASE_GOAL_LIVING',
  RENTAL = 'PURCHASE_GOAL_RENTAL',
  // DEV-784: сценарии анкеты лендингов
  COMBINED = 'PURCHASE_GOAL_COMBINED',
  MARKET_RESEARCH = 'PURCHASE_GOAL_MARKET_RESEARCH',
  // DEV-1093: «рост стоимости» и «сохранение капитала» — отдельные ответы квиза,
  // раньше оба сводились к INVESTMENT и в карточке было не видно, что выбрал клиент
  VALUE_GROWTH = 'PURCHASE_GOAL_VALUE_GROWTH',
  CAPITAL_PRESERVATION = 'PURCHASE_GOAL_CAPITAL_PRESERVATION',
}

/** Timeline сделки - соответствует V1DealTimeline из proto */
export enum DealTimeline {
  INVALID = 'DEAL_TIMELINE_INVALID',
  URGENT = 'DEAL_TIMELINE_URGENT',
  // DEV-1093: без своего значения «1–3 месяца» схлопывался в URGENT
  ONE_TO_THREE_MONTHS = 'DEAL_TIMELINE_ONE_TO_THREE_MONTHS',
  THREE_TO_SIX_MONTHS = 'DEAL_TIMELINE_THREE_TO_SIX_MONTHS',
  MORE_THAN_SIX_MONTHS = 'DEAL_TIMELINE_MORE_THAN_SIX_MONTHS',
  // DEV-784: «мягкие» сроки из анкеты лендингов
  LATER = 'DEAL_TIMELINE_LATER',
  RESEARCHING = 'DEAL_TIMELINE_RESEARCHING',
}

/** Статус сделки */
export enum DealStatus {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  WON = 'WON',
  LOST = 'LOST',
}

// DEV-42: ClientStage enum упразднён вместе с канбаном клиента.
// Вся воронка теперь живёт в backend/deals (StageType, 15 этапов).

/** Статус оплаты */
export enum PaymentStatus {
  NOT_PAID = 'NOT_PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  FULLY_PAID = 'FULLY_PAID',
}

/** Тип финансирования */
export enum FundingType {
  CASH = 'CASH',
  MORTGAGE = 'MORTGAGE',
  MIXED = 'MIXED',
}

/** Заметка менеджера */
export interface ManagerNote {
  id: string
  content: string
  createdAt: string
  updatedAt?: string
  managerId: string
  isImportant?: boolean
  authorId?: string
  authorName?: string
}

/** DEV-240. Задача клиента (упрощённая модель: без этапов/статусов). */
export interface ClientTask {
  id: string
  clientId: string
  description: string
  dueDate?: string
  /** Время в dueDate указано пользователем (false = «в течение дня»). */
  dueHasTime?: boolean
  /** За сколько минут до срока напомнить (0 = без напоминания). */
  reminderMinutes?: number
  /** Исполнитель (дефолт на бэке — ответственный за клиента). */
  assigneeManagerId?: string
  completed: boolean
  completedAt?: string
  createdAt?: string
  updatedAt?: string
  /** DEV-1991. Тип задачи из справочника; пусто — тип не проставлен. */
  taskTypeId?: string
  /** Имя и цвет типа — снимок для чипа, чтобы карточка не ходила в справочник. */
  taskTypeName?: string
  taskTypeColor?: string
  taskTypeIcon?: string
}

/** DEV-240. Создание задачи клиента. */
export interface CreateClientTaskRequest {
  clientId: string
  description: string
  dueDate?: string
  dueHasTime?: boolean
  reminderMinutes?: number
  /** Пусто — бэкенд поставит ответственного за клиента. */
  assigneeManagerId?: string
  /** DEV-1991. Тип задачи из справочника; пусто — без типа. */
  taskTypeId?: string
}

/** DEV-240. Частичное обновление задачи клиента. */
export interface UpdateClientTaskRequest {
  clientId: string
  taskId: string
  description?: string
  dueDate?: string
  dueHasTime?: boolean
  /** DEV-1991. Снять срок. Пустой dueDate значит «не трогать», а не «убрать». */
  clearDueDate?: boolean
  reminderMinutes?: number
  /** Пусто — не менять исполнителя. */
  assigneeManagerId?: string
  completed?: boolean
  /** DEV-1991. Тип задачи; не задан — не менять. */
  taskTypeId?: string
}

/** Взаимодействие с клиентом */
export interface Interaction {
  id: string
  type: string
  date: string
  description: string
  managerId: string
}

/** Комментарий к origin клиента */
export interface OriginComment {
  id?: string
  comment?: string
  createdAt?: string
  updatedAt?: string
}

/** Origin клиента (источник с комментариями) */
export interface ClientOrigin {
  id?: string
  origin?: string
  /** Маркер CTA, с которого пришла заявка (DEV-784). */
  marker?: string
  /** Слаг лендинга-источника (DEV-659). Пуст у заявок не с лендинга. */
  landingSlug?: string
  /** @deprecated Использовать comments. Содержит первый комментарий для обратной совместимости. */
  comment?: string
  comments?: OriginComment[]
  createdAt?: string
  updatedAt?: string
  entityType?: string
  entityId?: string
  source?: string
  timestamp?: string
}

/** Клиент (основная модель) */
export interface Client {
  id: string
  createdAt?: string
  updatedAt?: string

  // Личная информация
  firstName: string
  lastName: string
  photoUrl?: string
  dateOfBirth?: string

  // Локация
  countryId?: string
  country?: Country
  cityId?: string // ID города из справочника client-data
  cityName?: string // Название города по cityId из справочника client-data (read-only, DEV-851)
  city?: string // Название города (если cityId = null)
  language?: string

  // Контакты
  contacts: Contact[]
  preferredContactMethod?: ContactType

  // Источник лида (DEV-42: clientStatusId и stage упразднены)
  leadSourceId?: string

  // Предпочтения
  interestedPropertyTypes?: PropertyType[]
  interestedLocations?: string[]
  budget?: Budget
  purchaseGoal?: PurchaseGoal
  dealTimeline?: DealTimeline
  /** Доля первоначального взноса в процентах, 0–100 (DEV-1093). */
  downPaymentPercent?: number
  additionalCriteria?: string[]

  // История взаимодействий
  firstContactDate?: string
  interactions?: Interaction[]
  sentProposals?: string[]
  documentIds?: string[]
  managerNotes?: ManagerNote[]

  // Сделка (DEV-42: currentDealStatus упразднён — статус живёт в backend/deals)
  currentDealId?: string
  responsibleManagerId?: string
  expectedCommission?: number
  paymentStatus?: PaymentStatus
  fundingType?: FundingType

  // Дополнительно
  isSubscribedToNewsletter?: boolean
  telegramBindingId?: string
  origins?: ClientOrigin[]
  /**
   * Площадка, с которой пришёл клиент (DEV-1870). Только на чтение: бэкенд
   * денормализует её из origins, потому что список клиентов их не тянет.
   */
  landingSlug?: string

  // Агент, приведший клиента (DEV-232). UUID пользователя auth с ролью agent.
  // Заполняется только когда клиента создал агент через агентскую панель.
  agentId?: string

  // DEV-473 (§7.3). Агентский источник клиента: тип источника, компания, snapshot ФИО агента/
  // названия компании, модель, дата регистрации в панели, комментарий агента, статус дубля.
  clientSourceType?: string
  companyId?: string
  agentNameSnapshot?: string
  companyNameSnapshot?: string
  agentParticipationModel?: string
  agentClientRegisteredAt?: string
  agentComment?: string
  agentDuplicateStatus?: string
  /** @deprecated DEV-1942: причина переехала в duplicateClaims, бэкенд её больше не пишет. */
  agentDuplicateReason?: string
  /**
   * DEV-1942. Заявки агентов на дубль этой карточки. Приезжают только в карточке
   * (в списке их нет — виджет дублей фильтрует по agentDuplicateStatus).
   */
  duplicateClaims?: ClientDuplicateClaim[]
  /** DEV-1918. Номер заявки агентской панели, из которой пришёл клиент. */
  agentPanelClientRequestId?: string

  // Архивация
  isArchived?: boolean
  archivedAt?: string
  /** Комментарий к причине архивации (свободный текст). */
  archiveReason?: string
  /** DEV-1061: причина архивации из справочника archive_reasons. */
  archiveReasonId?: string
  /** DEV-1061: машинный ключ причины (NO_DIALOG, REFUSED, ...), read-only. */
  archiveReasonKey?: string
  /** DEV-1061: локализованное название причины, read-only. */
  archiveReasonName?: string

  // Теги (DEV-231/DEV-232): «Агентский» (AGENT_LEAD) / «Дубль» (DUPLICATE)
  tags?: ClientTag[]
}

/** Тег клиента (справочник tags + client_tags). Ключи: AGENT_LEAD, DUPLICATE. */
export interface ClientTag {
  id: string
  key: string
  nameRu?: string
  nameEn?: string
  nameVn?: string
}

/**
 * DEV-1942. Заявка агента на дубль клиента: агент прислал в панель клиента,
 * который уже есть в базе. Разбирает РОП — «передать этому агенту» либо
 * «оставить как есть» (право clients:assign_agent).
 */
export interface ClientDuplicateClaim {
  id: string
  /** user_id агента: по нему открывается его карточка в CRM (/dashboard/agents/{id}). */
  agentId: string
  /** По чему совпало: 'phone' | 'email'. */
  matchedField: string
  /** 'pending' | 'confirmed' | 'rejected'. */
  status: string
  createdAt?: string
}

/** Фильтры для списка клиентов */
export interface ClientsFilter {
  search?: string
  // DEV-2301: ветки «Источника» (источник → площадка → кампания), любая из них.
  // Ключ ветки понимает client-data (source_keys); id источника — ветка верхнего уровня.
  sourceKeys?: string[]
  countryId?: string
  // DEV-853: фильтр по городам — id справочника client-data, несколько id = OR.
  cityIds?: string[]
  language?: string
  responsibleManagerId?: string
  // DEV-232: фильтр по агенту, приведшему клиента (clients.agent_id).
  agentId?: string
  propertyTypes?: PropertyType[]
  dealTimeline?: DealTimeline
  minBudget?: number
  maxBudget?: number
  createdAfter?: string
  createdBefore?: string
  hasDeal?: boolean // true = со сделкой, false = без сделки, undefined = все
  // DEV-142: true = есть ответственный менеджер, false = ответственного нет, undefined = без фильтра.
  hasResponsibleManager?: boolean
  // DEV-281: true = клиент пришёл через агента (agent_id IS NOT NULL), false = без агента, undefined = без фильтра.
  hasAgent?: boolean
  // DEV-1005: пресет «Дубли» — статус дубля от агента (agent_duplicate_status), напр. 'duplicate_pending'.
  agentDuplicateStatus?: string
  // DEV-1062: фильтр по тегам клиента (WARMING/TEST/...), несколько ключей = OR.
  tagKeys?: string[]
  // DEV-1061: фильтр по причине архивации (id справочника archive_reasons).
  archiveReasonIds?: string[]
  // DEV-1061: показать архивных (по умолчанию листинг отдаёт только активных).
  isArchived?: boolean
}

/** Параметры пагинации */
export interface PaginationParams {
  page: number
  pageSize: number
}

/** Результат списка с пагинацией */
export interface PaginatedClients {
  clients: Client[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

/** Данные для создания клиента */
export interface CreateClientRequest {
  // Основная информация
  firstName: string
  lastName: string
  contacts: Contact[]
  countryId?: string
  cityId?: string
  city?: string
  language?: string
  leadSourceId?: string
  preferredContactMethod?: ContactType
  dateOfBirth?: string
  /** DEV-699. Ответственный менеджер (опционально): пусто — автораспределение.
   * Создатель клиента менеджером автоматически не становится (support_manager). */
  responsibleManagerId?: string

  // Интересы и потребности
  interestedPropertyTypes?: PropertyType[]
  interestedLocations?: string[]
  budget?: Budget
  purchaseGoal?: PurchaseGoal
  dealTimeline?: DealTimeline
  /** Доля первоначального взноса в процентах, 0–100 (DEV-1093). */
  downPaymentPercent?: number
  additionalCriteria?: string[]

  // Маркетинг
  isSubscribedToNewsletter?: boolean
}

/** Данные для обновления клиента */
export interface UpdateClientRequest {
  id: string
  firstName?: string
  lastName?: string
  photoUrl?: string
  dateOfBirth?: string
  countryId?: string
  cityId?: string
  city?: string
  language?: string
  contacts?: Contact[]
  preferredContactMethod?: ContactType
  leadSourceId?: string
  responsibleManagerId?: string
  interestedPropertyTypes?: PropertyType[]
  interestedLocations?: string[]
  budget?: Budget
  purchaseGoal?: PurchaseGoal
  dealTimeline?: DealTimeline
  /** Доля первоначального взноса в процентах, 0–100 (DEV-1093). */
  downPaymentPercent?: number
  additionalCriteria?: string[]
  isSubscribedToNewsletter?: boolean
  isArchived?: boolean
  archiveReason?: string
  /** DEV-1061: причина архивации из справочника; обязательна при isArchived=true. */
  archiveReasonId?: string
  /**
   * DEV-1918. Канал привлечения (Direct/Marketing/Referral/Other). Менять может только
   * обладатель clients:assign_agent; значение Agent через UpdateClient бэкенд отклоняет —
   * агента назначают через agentsApi.setClientAgent.
   */
  clientSourceType?: string
}

// DEV-42: Client Tasks упразднены — все задачи живут в backend/deals.

// ===================== Client Change Logs =====================

/**
 * Тип действия в истории изменений клиента
 */
export enum ClientChangeActionType {
  INVALID = 'CLIENT_CHANGE_ACTION_TYPE_INVALID',
  CREATED = 'CLIENT_CHANGE_ACTION_TYPE_CREATED',
  UPDATED = 'CLIENT_CHANGE_ACTION_TYPE_UPDATED',
  STAGE_CHANGED = 'CLIENT_CHANGE_ACTION_TYPE_STAGE_CHANGED',
  STATUS_CHANGED = 'CLIENT_CHANGE_ACTION_TYPE_STATUS_CHANGED',
  TASK_CREATED = 'CLIENT_CHANGE_ACTION_TYPE_TASK_CREATED',
  TASK_UPDATED = 'CLIENT_CHANGE_ACTION_TYPE_TASK_UPDATED',
  TASK_COMPLETED = 'CLIENT_CHANGE_ACTION_TYPE_TASK_COMPLETED',
  TASK_DELETED = 'CLIENT_CHANGE_ACTION_TYPE_TASK_DELETED',
  NOTE_ADDED = 'CLIENT_CHANGE_ACTION_TYPE_NOTE_ADDED',
  NOTE_UPDATED = 'CLIENT_CHANGE_ACTION_TYPE_NOTE_UPDATED',
  NOTE_DELETED = 'CLIENT_CHANGE_ACTION_TYPE_NOTE_DELETED',
  MANAGER_ASSIGNED = 'CLIENT_CHANGE_ACTION_TYPE_MANAGER_ASSIGNED',
  CONTACT_ADDED = 'CLIENT_CHANGE_ACTION_TYPE_CONTACT_ADDED',
  CONTACT_REMOVED = 'CLIENT_CHANGE_ACTION_TYPE_CONTACT_REMOVED',
  DELETED = 'CLIENT_CHANGE_ACTION_TYPE_DELETED',
}

/**
 * Метаданные записи в истории изменений
 */
export interface ClientChangeLogDetails {
  field_label?: string
  old_value_label?: string
  new_value_label?: string
  manager_name?: string
}

/**
 * Запись в истории изменений клиента
 */
export interface ClientChangeLog {
  id: string
  clientId: string
  actionType: ClientChangeActionType
  performedByManagerId?: string
  performedByManagerName?: string
  fieldName?: string
  oldValue?: string
  newValue?: string
  detailsJson?: string
  details?: ClientChangeLogDetails
  relatedEntityId?: string
  relatedEntityType?: string
  createdAt: string
}

/**
 * Файл клиента
 */
export interface ClientFile {
  id: string
  clientId: string
  fileId: string
  filename: string
  displayName: string
  description?: string
  fileSize: number
  mimeType: string
  uploadedByManagerId?: string
  createdAt: string
  /** Источник файла: upload | telegram | whatsapp (DEV-974) */
  source?: string
}

/**
 * DEV-331. Ошибка обработки одной строки файла импорта.
 */
export interface ImportRowError {
  line: number
  reason: string
}

/**
 * DEV-331. Итог импорта клиентов из файла (ответ POST /api/v1/clients/import).
 */
export interface ImportResult {
  total: number
  created: number
  merged: number
  skipped: number
  failed: number
  errors?: ImportRowError[]
}
